// Phase 123 — Folder cascade-delete confirmation dialog.
//
// When a teacher deletes a folder that has activities or child folders
// inside, this dialog appears so they can explicitly pick what happens:
//   • Unfile (default, safe): activities move to Unfiled; child Unit
//     folders are deleted (their activities also get unfiled).
//   • Reparent (rare): child Unit folders are lifted to top-level
//     Subjects; loose activities in the parent go to Unfiled.
//   • Delete all (destructive): child folders AND every activity in
//     the entire tree are deleted. Confirmed with a typed "DELETE"
//     guardrail to prevent misclicks.
//
// Empty folders (zero activities, zero child folders) bypass this
// dialog and delete immediately with cascade=unfile.
//
// Used by the FolderSidebar (Library) when handleDeleteFolder is
// invoked on a non-empty folder.

import { useEffect, useState } from "react";
import { AlertTriangle, FolderInput, Inbox, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const MODES = [
  {
    id: "unfile",
    icon: Inbox,
    title: "Move contents to Unfiled",
    blurb:
      "Activities go back to Unfiled where you can refile them later. Child Unit folders are deleted (their activities also unfile).",
    confirmLabel: "Delete folder · keep activities",
    tone: "safe",
  },
  {
    id: "reparent",
    icon: FolderInput,
    title: "Lift child Unit folders to top level",
    blurb:
      "Child Unit folders become Subject folders. Activities directly in this folder go to Unfiled.",
    confirmLabel: "Delete folder · promote children",
    tone: "neutral",
  },
  {
    id: "delete_all",
    icon: Trash2,
    title: "Delete everything in this folder",
    blurb:
      "Permanently deletes child folders AND every activity inside the tree. The underlying lesson/test data stays in your account but the Library cards disappear.",
    confirmLabel: "Delete folder + contents",
    tone: "destructive",
  },
];

export function CascadeDeleteDialog({
  open,
  folder,
  activityCount,
  childFolderCount,
  batchCount = 1,
  extraLessonCount = 0,
  onCancel,
  onConfirm,
}) {
  const [mode, setMode] = useState("unfile");
  const [typedConfirm, setTypedConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const isBatch = batchCount > 1;

  // Reset state every time the dialog opens for a new folder.
  useEffect(() => {
    if (open) {
      setMode("unfile");
      setTypedConfirm("");
      setBusy(false);
    }
  }, [open, folder?.id, batchCount]);

  if (!folder && !isBatch) return null;

  const destructive = mode === "delete_all";
  const needsTyped = destructive;
  const confirmReady = !needsTyped || typedConfirm.trim().toUpperCase() === "DELETE";
  const selectedMode = MODES.find((m) => m.id === mode);

  const handleConfirm = async () => {
    if (!confirmReady) return;
    setBusy(true);
    try {
      await onConfirm(mode);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && !busy && onCancel?.()}>
      <AlertDialogContent
        data-testid="cascade-delete-dialog"
        className="sm:max-w-lg"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 font-black text-[#1c2d3d]">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {isBatch
              ? `Delete ${batchCount} folders?`
              : <>Delete &quot;{folder?.name}&quot;?</>}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[#4a6984] leading-relaxed">
            {isBatch ? "These folders contain " : "This folder contains "}
            <strong className="text-[#1c2d3d]">
              {activityCount} {activityCount === 1 ? "activity" : "activities"}
            </strong>
            {childFolderCount > 0 ? (
              <>
                {" "}and{" "}
                <strong className="text-[#1c2d3d]">
                  {childFolderCount} child Unit folder
                  {childFolderCount === 1 ? "" : "s"}
                </strong>
              </>
            ) : null}
            . Choose what should happen to them.
            {extraLessonCount > 0 ? (
              <>
                {" "}
                <span className="text-red-700 font-semibold">
                  {extraLessonCount} separately-selected lesson
                  {extraLessonCount === 1 ? "" : "s"} will also be deleted.
                </span>
              </>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2" data-testid="cascade-modes">
          {MODES.map((m) => {
            const Icon = m.icon;
            const selected = mode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                data-testid={`cascade-mode-${m.id}`}
                onClick={() => setMode(m.id)}
                className={
                  "w-full text-left rounded-lg border p-3 flex items-start gap-3 transition-colors " +
                  (selected
                    ? m.tone === "destructive"
                      ? "border-red-400 bg-red-50"
                      : "border-[#96ded1] bg-[#96ded1]/15"
                    : "border-[#4a6984]/20 bg-white hover:bg-[#f4f5f7]")
                }
              >
                <div
                  className={
                    "h-9 w-9 rounded-md flex items-center justify-center shrink-0 " +
                    (m.tone === "destructive"
                      ? "bg-red-100 text-red-600"
                      : "bg-[#1c2d3d]/5 text-[#1c2d3d]")
                  }
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={
                      "font-extrabold text-sm " +
                      (m.tone === "destructive" ? "text-red-700" : "text-[#1c2d3d]")
                    }
                  >
                    {m.title}
                  </p>
                  <p className="mt-0.5 text-xs text-[#4a6984] leading-snug">
                    {m.blurb}
                  </p>
                </div>
                <span
                  className={
                    "h-4 w-4 rounded-full border shrink-0 mt-0.5 " +
                    (selected
                      ? m.tone === "destructive"
                        ? "border-red-500 bg-red-500"
                        : "border-[#10b981] bg-[#10b981]"
                      : "border-[#4a6984]/40")
                  }
                />
              </button>
            );
          })}
        </div>

        {needsTyped && (
          <div className="mt-3" data-testid="cascade-typed-confirm-wrap">
            <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-red-700 mb-1">
              Type DELETE to confirm
            </label>
            <input
              type="text"
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              data-testid="cascade-typed-confirm"
              autoComplete="off"
              className="w-full h-9 rounded-md border border-red-400 bg-white px-3 text-sm font-bold text-red-700 placeholder:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-300"
              placeholder="DELETE"
            />
          </div>
        )}

        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel
            disabled={busy}
            data-testid="cascade-cancel"
            className="border-[#4a6984]/20 text-[#4a6984]"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={!confirmReady || busy}
            data-testid="cascade-confirm"
            onClick={handleConfirm}
            className={
              "font-extrabold uppercase tracking-[0.14em] text-xs " +
              (destructive
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-[#1c2d3d] hover:bg-[#0e1a25] text-white")
            }
          >
            {busy ? "Working…" : selectedMode?.confirmLabel || "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default CascadeDeleteDialog;
